import { Ace } from 'ace-builds';
import variablesManager from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import { WidgetType, WidgetGroupState } from '@kemu-io/kemu-core/types';
import { VariableWidgetState } from '@kemu-io/kemu-core/widgets/variable/index.js';
import capitalize from 'capitalize';
import getBoundingBoxText from './completions/getBoundingBox';
import { getWidgetsInThing } from '@src/app/recipe/utils';
import { dataTypeToRawString } from '@common/utils';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';

type ScriptContext = {
	recipeId: string;
	thingRecipeId: string;
	widgetId: string;
}

let currentContext: ScriptContext | undefined;

export const setCompletionContext = (context: ScriptContext) => {
	currentContext = {
		...context,
	};
};


	// https://github.com/TouK/nussknacker/blob/v0.0.9/ui/client/components/graph/ExpressionSuggest.js#L31
export const customAceEditorCompleter: Ace.Completer = {
    getCompletions: (
			editor: Ace.Editor,
			session: Ace.EditSession,
			caretPosition2d: Ace.Point,
			prefix: string,
			callback: Ace.CompleterCallback
		) => {
			const completions: Ace.Completion[] = [];
			const keywords = ['getWidgetInputs', 'getWidgetOutputs', 'processEvent'];

			completions.push({
				caption: 'getVariable',
				snippet: `const { value, type } = Kemu.variable.get('\${1:variableName}', \${2:'defaultValue'});`,
				docText: 'Reads the current value of a variable and returns the default value if the variable does not exist.',
				meta: 'Kemu function',
				score: 100,
			});

			completions.push({
				caption: 'getUIValue',
				snippet: `const { value, type } = Kemu.variable.getUIValue('\${1:variableName}');`,
				docText: 'Reads the current value of a UI element in the parent widget. This method will throw if the script is not inside a widgetGroup or if the variable is not assigned to a field in the parent widget.',
				meta: 'Kemu function',
				score: 100,
			});

			completions.push({
				caption: 'setUIValue',
				snippet: `await Kemu.variable.setUIValue('\${1:fieldName}', \${2:value});`,
				docText: 'Sets the value of a UI field in the parent widget group. The control (e.g. text field, checkbox) updates to show the new value. Throws if not inside a widget group.',
				meta: 'Kemu function',
				score: 100,
			});

			completions.push({
				caption: 'setVariable',
				snippet: `await Kemu.variable.set('\${1:variableName}', \${2:value}, \${3:/* DataType.String */});`,
				docText: 'Sets the value of a variable',
				meta: 'Kemu function',
				score: 100,
			});

			// Fill up list of variables
			if (currentContext) {
				const variables = variablesManager.getVariableNames();

				const thingWidgets = getWidgetsInThing(currentContext.recipeId, currentContext.thingRecipeId);
				const getWidgetById = (id: string) => thingWidgets[id];
				const t = getGlobalIntl();

				variables.forEach((variable) => {
					const owner = getWidgetById(variable.subscriberWidgetId);
					const widgetName = owner.type === WidgetType.widgetGroup
						? (owner.state as WidgetGroupState).name
						: (owner.state as VariableWidgetState).name;

					const widgetType = capitalize(owner.type);
					const varType = dataTypeToRawString(t, variable.type);
					completions.push({
						value: variable.name,
						score: 101,
						meta: 'variable',
						caption: `${widgetName} -> ${variable.name}`,
						docText: `Variable "${variable.name}" comes from ${widgetType} "${widgetName}" (${varType})`,
					});
				});
			}

			keywords.forEach((word) => {
				if (word.startsWith(prefix)) {
					completions.push({
						value: word,
						score: 1,
						// TODO: 'Add documentation?
						meta: `Kemu function`,
					});
				}
			});

			completions.push({
				caption: 'Kemu.helpers.http (basic)',
				snippet: `const request = Kemu.helpers.http({
	method: '\${1:GET}',
	url: '\${2:https://api.example.com}',
	responseType: '\${3:json}',
});
const response = await request;
if(response && response.error){
	console.error('HTTP error:', response.error.message);
}else if(response){
	console.log('HTTP results:', response.results);
}`,
				docText: 'Perform an HTTP request via Hub Service. The Hub Desktop App must be running. Resolves to { results } or { error } (no throw).',
				meta: 'Kemu HTTP helper',
				score: 100,
			});

			completions.push({
				caption: 'Kemu.helpers.http (stream + abort)',
				snippet: `const req = Kemu.helpers.http({
	method: '\${1:GET}',
	url: '\${2:https://api.example.com/stream}',
	responseType: 'text',
	streamResponses: true,
	onStream: (evt) => {
		if(evt.phase === 'start'){
			console.log('STREAM start:', evt.statusCode, evt.headers);
		}else if(evt.phase === 'data'){
			console.log('STREAM data:', evt.sequence, evt.size);
		}else if(evt.phase === 'end'){
			console.log('STREAM end:', evt.bytesReceived, evt.statusCode);
		}
	},
});
// Abort later if needed
await req.abort();
// Or await the final result
const res = await req;
if(res && res.error){
	console.error('HTTP error:', res.error.message);
}`,
				docText: 'Enable streaming with streamResponses=true and handle phases using onStream. The returned promise has an abort() method.',
				meta: 'Kemu HTTP helper',
				score: 100,
			});

			completions.push({
				caption: 'Kemu.helpers.http (ActionConfig)',
				snippet: `const httpConfig = {
	method: '\${1:POST}',
	url: '\${2:https://api.example.com}',
	headers: \${3:{ 'content-type': 'application/json' }},
	query: \${4:{ page: 1 }},
	body: \${5:{ foo: 'bar' }},
	responseType: '\${6:json}',
	responseEncoding: '\${7:utf8}',
	followRedirects: \${8:true},
	decompress: \${9:true},
	streamResponses: \${10:false},
	onStream: \${11:(evt) => { /* requires streamResponses: true */ }},
};
const res = await Kemu.helpers.http(httpConfig);`,
				docText: 'ActionConfig fields for Kemu.helpers.http.',
				meta: 'Kemu HTTP helper',
				score: 95,
			});

			/**
			  getBoundingBox,
				getPixelValueAtIndex,
				copyImageData,
				setPixelValueAtIndex
			 */

			completions.push({
				value: 'const imageCopy = Kemu.image.copyImageData(imageData);',
				score: 1,
				meta: 'copyImageData',
			});

			completions.push({
				caption: 'decodeDataType',
				snippet: 'Kemu.helpers.decodeDataType(${1:object});',
				docText: 'Utility to determine the equivalent Kemu DataType at runtime',
				score: 1,
				meta: 'decodeDataType',
			});

			completions.push({
				value: `Kemu.image.setPixelValueAtIndex(
		imageData,
		0 /* index */, 
		{ /* RGB values or a number between 0 and 255 to set all 3 channels at once */ 
			r: 255,
			g: 255,
			b: 255,
		}
	);`,
				score: 1,
				meta: 'setPixelValueAtIndex',
			});

			completions.push({
				value: 'const { avr, r, g, b } = Kemu.image.getPixelValueAtIndex(imageData, index);',
				score: 1,
				meta: 'getPixelValueAtIndex',
			});

			completions.push({
				value: getBoundingBoxText(),
				score: 1,
				meta: 'getBoundingBox',
			});

		completions.push({
			value: `Kemu.variable.onValueChange('myVariable', async ({ value, type }) => {
	console.log(\`Variable of type \${type} changed to: \`, value);
});`,
			score: 1,
			docText: 'Subscribe to variable changes',
			meta: 'onValueChange',
		});

		completions.push({
			value: `Kemu.variable.onUIValueChange('uiVarName', async ({ value, type }) => {
	console.log(\`UI control of type \${type} changed to: \`, value);
});`,
			score: 1,
			docText: 'Subscribe to variable changes',
			meta: 'onValueChange',
		});

		completions.push({
			caption: 'globalVars.get',
			snippet: `const globalVar = Kemu.globalVars.get('\${1:globalVarName}');
if (globalVar) {
	console.log(\`Value: \${globalVar.value}, Type: \${globalVar.type}\`);
}`,
			docText: 'Gets the value of a global variable. Returns an object with name, value, type, and controlType, or null if not found.',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'globalVars.set',
			snippet: `await Kemu.globalVars.set({
	name: '\${1:globalVarName}',
	value: \${2:value},
	type: DataType.\${3:String},
	controlType: '\${4:text}',
});`,
			docText: 'Sets or creates a global variable. Can include helpText and order for UI display.',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'globalVars.set (with helpText and order)',
			snippet: `await Kemu.globalVars.set({
	name: '\${1:configValue}',
	value: \${2:'default'},
	helpText: '\${3:Configuration value}',
	order: \${4:1},
});`,
			docText: 'Sets a global variable with helpText and display order.',
			meta: 'Kemu function',
			score: 98,
		});

		completions.push({
			value: `Kemu.globalVars.onValueChange('myGlobal', async ({ name, value, type, controlType }) => {
	console.log(\`Global variable '\${name}' changed to: \${value} (type: \${type})\`);
});`,
			score: 1,
			docText: 'Subscribe to changes for a specific global variable',
			meta: 'onValueChange',
		});

		completions.push({
			value: `Kemu.globalVars.onAnyValueChange(async ({ name, value, type }) => {
	console.log(\`Global variable '\${name}' changed to: \${value}\`);
});`,
			score: 1,
			docText: 'Subscribe to changes for any global variable',
			meta: 'onAnyValueChange',
		});

		completions.push({
			caption: 'sendToPort',
			snippet: `await sendToPort('\${1:portName}', {
	type: DataType.\${2:dataType},
	value: \${3:value},
})`,
			docText: 'Sends data to the given port name',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'secrets.requestAccess',
			snippet: `Kemu.secrets.requestAccess('\${1:secretName}');`,
			docText: 'Requests access to a secret. This should be called before attempting to read the secret, ideally at the top of the script.',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'secrets.read',
			snippet: `const secrets = await Kemu.secrets.read(['\${1:secretName}']);`,
			docText: 'Reads the values of one or more secrets. Returns a promise that resolves to an object mapping secret names to their values (string) or null if not mapped.',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'secrets.readMultiple',
			snippet: `const secrets = await Kemu.secrets.read(['\${1:secret1}', '\${2:secret2}']);
if (secrets['\${1:secret1}'] && secrets['\${2:secret2}']) {
	// Use the secrets
	console.log('Secret 1:', secrets['\${1:secret1}']);
	console.log('Secret 2:', secrets['\${2:secret2}']);
} else {
	console.log('One or more secrets not configured');
}`,
			docText: 'Reads multiple secrets at once and checks if they are all available before using them.',
			meta: 'Kemu pattern',
			score: 98,
		});

		completions.push({
			caption: 'status.set',
			snippet: `Kemu.status.set('\${1:statusName}', \${2:value});`,
			docText: 'Sets the value of a status that appears in the Status Panel. Value can be a primitive (DataType auto-detected) or a PartialData object.',
			meta: 'Kemu function',
			score: 100,
		});

		completions.push({
			caption: 'secrets.setup',
			snippet: `// Request access to secrets at the top of your script
Kemu.secrets.requestAccess('api-key');
Kemu.secrets.requestAccess('database-url');

// Use them later in your code
const secrets = await Kemu.secrets.read(['api-key', 'database-url']);
const apiKey = secrets['api-key'];
const dbUrl = secrets['database-url'];`,
			docText: 'Common pattern for setting up and using secrets in your script.',
			meta: 'Kemu pattern',
			score: 95,
		});

		completions.push({
			caption: 'status.setWithType',
			snippet: `Kemu.status.set('\${1:statusName}', { type: DataType.\${2:dataType}, value: \${3:value} });`,
			docText: 'Sets a status value with an explicit data type using a PartialData object.',
			meta: 'Kemu function',
			score: 98,
		});

		completions.push({
			caption: 'status.initialize',
			snippet: `Kemu.status.set('\${1:statusName}', null); // Initialize status (appears in panel but shows no value)`,
			docText: 'Initializes a status without a value - it will appear in the Status Panel but show no value until set.',
			meta: 'Kemu pattern',
			score: 90,
		});


// 			completions.push({
// 				value: `
// const requiredFields = {
// 	region: 'us-east-1',
// 	accessKey: 'YOUR_ACCESS_KEY,
// 	secretKey: 'YOUR_SECRET_KEY,
// 	tableName: 'Table ABC'
// };

// const optionalFields = {
// 	limit: 100,
// 	indexName: undefined,
// 	roleArn: undefined,
// 	externalId: undefined,
// 	indexName: undefined,
// 	lastKey: undefined,
// 	filterExpression: undefined,
// 	// projectionExpression: '#id, #name',
// 	// expressionAttributeNames: { '#name': 'name', '#id': 'id' },
// 	lastKey: undefined,
// }

// const result = await Kemu.services.dynamoDb.scan({ 
// 	...requiredFields,
// 	...optionalFields,	
// });

// if(result.success){
// 	console.log(\`Results: \${JSON.stringify(result.data)}\`);
// }else{
// 	console.log(\`Failed to fetch results: \${result.data}\`);
// }
// 				`,
// 				score: 1,
// 				meta: 'Kemu Dynamo service',
// 			});

			callback(null, completions);
      // const suggestions = this.expressionSuggester.suggestionsFor(this.state.value, caretPosition2d)
      // callback(null, suggestions?.map((s: any) => {
      //   //unfortunately Ace treats `#` as special case, we have to remove `#` from suggestions or it will be duplicated
      //   //maybe it depends on language mode?
      //   const methodName = s.methodName.replace("#", "")
      //   const returnType = ProcessUtils.humanReadableType(s.refClazz)
      //   return {
			// 		name: methodName, 
			// 		value: methodName, 
			// 		score: 1,
			// 		meta: returnType, 
			// 		description: s.description, 
			// 		parameters: s.parameters, 
			// 		returnType: returnType
			// 	}
      // }))
    },
    getDocTooltip: (item: any) => {
			return undefined;
			// console.log('Tooltip for item ', item);
      // if (item?.description || !item?.parameters) {
      //   const paramsSignature = item.parameters.map(p => ProcessUtils.humanReadableType(p.refClazz) + " " + p.name).join(", ")
      //   const javaStyleSignature = `${item.returnType} ${item.name}(${paramsSignature})`
      //   item.docHTML = ReactDOMServer.renderToStaticMarkup((
      //     <div className="function-docs">
      //       <b>{javaStyleSignature}</b>
      //       <hr/>
      //       <p>{item.description}</p>
      //     </div>
      //   ))
      // }
    }
  };
